import {TruckIcon} from "@heroicons/react/20/solid";
import {
  type ContainerStatusIconSet,
  type ContainerStatusIconVariant,
  getContainerStatusIconSrc,
  useContainerStatusIconSet
} from "../../container-status/container-status-icons";
import {OtsTooltip} from "../../components/OtsTooltip";
import {useOtsColorScheme} from "../../theme/OtsTheme";

type Cargo = "water" | "oil";

const CARGO_LABEL: Record<Cargo, string> = {water: "Water", oil: "Oil"};

type IconOptions = {iconSet: ContainerStatusIconSet; variant: ContainerStatusIconVariant; colorScheme: "light" | "dark"};

/**
 * One cargo's route off the site. Pipeline is the container status grid's own icon, in the same set, variant and
 * size; the grid shows nothing for trucked cargo, so a truck in the cargo's color stands in.
 */
const TransportIcon = ({cargo, pipeline, icons}: {cargo: Cargo; pipeline: boolean; icons: IconOptions}) => {
  const label = `${CARGO_LABEL[cargo]} moves by ${pipeline ? "pipeline" : "truck"}`;
  return <OtsTooltip content={label} density="compact">
    <span role="img" aria-label={label} data-transport={`${cargo}-${pipeline ? "pipeline" : "truck"}`} className="inline-flex size-5 items-center justify-center">
      {pipeline
        ? <img
          alt=""
          width={16}
          height={16}
          src={getContainerStatusIconSrc(`${cargo}-pipeline`, icons.variant, icons.iconSet, icons.colorScheme)}/>
        : <TruckIcon
          aria-hidden="true"
          className={["size-4", cargo === "water" ? "text-[var(--ots-transport-category-water)]" : "text-[var(--ots-transport-category-oil)]"].join(" ")}/>}
    </span>
  </OtsTooltip>;
};

export type SiteTransportIconsProps = {
  waterViaPipeline: boolean;
  oilViaPipeline: boolean;
  /**
   * The container status icon set, as the grid's `statusIconSet`: FieldGo's, or the web's classic Tabler set.
   * Defaults to ContainerStatusIconSetProvider's, so one provider keeps the grid and the map in step.
   */
  iconSet?: ContainerStatusIconSet;
  /** As the grid's `statusIconVariant`. */
  variant?: ContainerStatusIconVariant;
};

/** How a site's water and oil leave it, as two icons; the tooltip says which is which. */
export const SiteTransportIcons = ({waterViaPipeline, oilViaPipeline, iconSet, variant = "filled"}: SiteTransportIconsProps) => {
  const contextSet = useContainerStatusIconSet();
  // The oil pipeline has a light-on-dark version, as in the grid.
  const icons: IconOptions = {iconSet: iconSet ?? contextSet, variant, colorScheme: useOtsColorScheme() ?? "light"};
  return <span className="inline-flex items-center gap-2" data-icon-set={icons.iconSet}>
    <TransportIcon cargo="water" pipeline={waterViaPipeline} icons={icons}/>
    <TransportIcon cargo="oil" pipeline={oilViaPipeline} icons={icons}/>
  </span>;
};
